import React, { useState, useCallback, useEffect, useRef } from 'react';
import { RentMap } from '../components/RentMap';
import { RentalListingsSidebar } from '../components/RentalListingsSidebar';
import { PinRentForm } from '../components/PinRentForm';
import { PostRoomForm } from '../components/PostRoomForm';
import { PostPropertyForm } from '../components/PostPropertyForm';
import { SeekRoomForm } from '../components/SeekRoomForm';
import { SeekPropertyForm } from '../components/SeekPropertyForm';
import { UserGuideModal } from '../components/UserGuideModal';
import { PrivacyPolicyModal } from '../components/PrivacyPolicyModal';
import { TermsOfUseModal } from '../components/TermsOfUseModal';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { RentPin } from '../types';
import { PinPopup } from '../components/PinPopup';
import { DeleteListingPopup } from '../components/DeleteListingPopup';
import rentalApi from '../services/api';
import { RentalListing, RentalSearch } from '../types';
import { track } from '../services/analytics';
import './HomePage.css';

type Panel = 'pinRent' | 'postRoom' | 'postProperty' | 'seekRoom' | 'seekProperty' | 'listings' | null;
type FormPanel = Exclude<Panel, 'listings' | null>;
type FilterTab = 'flats' | 'opps';

const PROP_TYPES = ['Basement', 'Apartment', 'Condo', 'Townhouse', 'Semi-Detached', 'Detached'];

const GTA_CITIES = [
  'Ajax', 'Aurora', 'Brampton', 'Burlington', 'Caledon', 'Cambridge',
  'East Gwillimbury', 'Georgina',
  'Hamilton', 'Kitchener', 'Markham', 'Mississauga', 'Milton',
  'Newmarket', 'Oakville', 'Oshawa', 'Pickering', 'Richmond Hill', 'Scarborough',
  'Toronto', 'Vaughan',
  'Whitchurch-Stouffville', 'Whitby',
];

const SEARCH_TYPES = ['geocode'];

interface FabItem {
  key: Panel;
  icon: string;
  color: string;
  bg: string;
  title: string;
  sub: string;
}

const FAB_MENU: FabItem[] = [
  { key: 'pinRent',      icon: '📍', color: '#E85D3C', bg: '#FBEBE4', title: 'Pin a Rental',      sub: 'Know a place? Drop a pin.' },
  { key: 'postRoom',     icon: '🏠', color: '#1A2744', bg: '#E8EDF5', title: 'Post a Room',        sub: 'List a room in your unit' },
  { key: 'postProperty', icon: '🏢', color: '#8FA987', bg: '#EEF4E9', title: 'Post Whole Unit',    sub: 'Rent out the whole place' },
  { key: 'seekRoom',     icon: '🔍', color: '#7C5CD3', bg: '#F0EBFC', title: 'Looking for Room',   sub: 'Find a roommate setup' },
  { key: 'seekProperty', icon: '🏡', color: '#D4902A', bg: '#FBF2E3', title: 'Looking for Unit',   sub: 'Find a whole rental' },
];

export const HomePage: React.FC = () => {
  const [activePanel, setActivePanel] = useState<Panel>(null);
  const [pendingPanel, setPendingPanel] = useState<FormPanel | null>(null);

  const [guideOpen, setGuideOpen] = useState(false);
  const [guideScreen, setGuideScreen] = useState<'welcome' | 'guide'>('welcome');

  useEffect(() => {
    track('page_view');
    if (localStorage.getItem('gta-onboarded') !== 'true') {
      setGuideScreen('welcome');
      setGuideOpen(true);
    }
  }, []);

  // Open PinPopup via delete link: /?pinId=xxx&deleteToken=yyy
  // Open DeleteListingPopup via delete link: /?listingId=xxx&deleteToken=yyy or /?searchId=xxx&deleteToken=yyy
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('deleteToken');
    const pinId = params.get('pinId');
    const listingId = params.get('listingId');
    const searchId = params.get('searchId');

    if (!token) return;
    window.history.replaceState({}, '', window.location.pathname);

    if (pinId) {
      rentalApi.getRentPinById(pinId)
        .then(res => { setTokenPopupPin(res.data.data); setTokenPopupToken(token); })
        .catch(() => {});
    } else if (listingId) {
      rentalApi.getRentalListingById(listingId)
        .then(res => setTokenListingEntity({ kind: 'listing', data: res.data.data, deleteToken: token }))
        .catch(() => {});
    } else if (searchId) {
      rentalApi.getRentalSearchById(searchId)
        .then(res => setTokenListingEntity({ kind: 'search', data: res.data.data, deleteToken: token }))
        .catch(() => {});
    }
  }, []);

  const [selectedCoords, setSelectedCoords] = useState<[number, number] | null>(null);
  const [selectedRental, setSelectedRental] = useState<RentPin | null>(null);
  const [detailRental, setDetailRental] = useState<RentPin | null>(null);
  const [tokenPopupPin, setTokenPopupPin] = useState<RentPin | null>(null);
  const [tokenPopupToken, setTokenPopupToken] = useState<string>('');
  type ListingEntity =
    | { kind: 'listing'; data: RentalListing; deleteToken: string }
    | { kind: 'search';  data: RentalSearch;  deleteToken: string }
    | null;
  const [tokenListingEntity, setTokenListingEntity] = useState<ListingEntity>(null);
  const [rentals, setRentals] = useState<RentPin[]>([]);
  const [sidebarHasMore, setSidebarHasMore] = useState(false);
  const [sidebarPage, setSidebarPage] = useState(0);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [priceFilter, setPriceFilter] = useState({ min: 0, max: 9999 });
  const [bedroomFilter, setBedroomFilter] = useState<string | null>(null);
  const [furnishFilter, setFurnishFilter] = useState<string | null>(null);
  const [propTypeFilter, setPropTypeFilter] = useState<string[]>([]);
  const [oppsAvailOnly, setOppsAvailOnly] = useState(false);
  const [filteredListingsCount, setFilteredListingsCount] = useState(0);
  const [showSuccess, setShowSuccess] = useState('');
  const [stats, setStats] = useState({ count: 0, total: 0, avg: 0 });
  const [mapRefreshKey, setMapRefreshKey] = useState(0);
  const [showNoResults, setShowNoResults] = useState(false);
  const openSidebarOnResultsRef = useRef(false);

  // ── City / search state ───────────────────────────────────────────────────
  const [selectedCity, setSelectedCity] = useState('');
  const [mapSearchValue, setMapSearchValue] = useState('');
  const [mapSearchCoords, setMapSearchCoords] = useState<[number, number] | null>(null);

  // ── Near Me ───────────────────────────────────────────────────────────────
  const [nearMeLocation, setNearMeLocation] = useState<[number, number] | null>(null);
  const [nearMeLoading, setNearMeLoading] = useState(false);
  const [showActionsPanel, setShowActionsPanel] = useState(true);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    if (sidebarLoading) return;
    if (rentals.length === 0 && (!!selectedCity || !!mapSearchValue)) {
      openSidebarOnResultsRef.current = false;
      setActivePanel(prev => prev === 'listings' ? null : prev);
      setShowNoResults(true);
      const t = setTimeout(() => setShowNoResults(false), 3500);
      return () => clearTimeout(t);
    } else {
      setShowNoResults(false);
      if (rentals.length > 0 && openSidebarOnResultsRef.current) {
        openSidebarOnResultsRef.current = false;
        setActivePanel('listings');
      }
    }
  }, [rentals.length, sidebarLoading, selectedCity, mapSearchValue]);

  // ── Transit ───────────────────────────────────────────────────────────────
  const [showTransit, setShowTransit] = useState(false);

  const handleNearMe = () => {
    if (!navigator.geolocation) return;
    track('near_me_click');
    setNearMeLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords: [number, number] = [pos.coords.longitude, pos.coords.latitude];
        setNearMeLocation(coords);
        setNearMeLoading(false);
        setShowActionsPanel(false);
      },
      () => setNearMeLoading(false),
      { timeout: 8000 }
    );
  };

  // ── Filter panel state ────────────────────────────────────────────────────
  const [showFilter, setShowFilter] = useState(false);
  const [filterTab, setFilterTab] = useState<FilterTab>('flats');

  // local draft state inside filter (committed on Done)
  const [draftCity, setDraftCity]           = useState('');
  const [draftBedroom, setDraftBedroom]     = useState<string | null>(null);
  const [draftPriceMin, setDraftPriceMin]   = useState('');
  const [draftPriceMax, setDraftPriceMax]   = useState('');
  const [draftFurnish, setDraftFurnish]     = useState<string | null>(null);
  const [draftPropTypes, setDraftPropTypes] = useState<string[]>([]);
  const [draftOppsAvailOnly, setDraftOppsAvailOnly] = useState(false);

  const openFilter = () => {
    setDraftCity(selectedCity);
    setDraftBedroom(bedroomFilter);
    setDraftPriceMin(priceFilter.min > 0 ? String(priceFilter.min) : '');
    setDraftPriceMax(priceFilter.max < 9999 ? String(priceFilter.max) : '');
    setDraftFurnish(furnishFilter);
    setDraftPropTypes(propTypeFilter);
    setDraftOppsAvailOnly(oppsAvailOnly);
    setShowFilter(true);
  };

  const applyFilter = () => {
    setSelectedCity(draftCity);
    setBedroomFilter(draftBedroom);
    setPriceFilter({
      min: draftPriceMin ? Number(draftPriceMin) : 0,
      max: draftPriceMax ? Number(draftPriceMax) : 9999,
    });
    setFurnishFilter(draftFurnish);
    setPropTypeFilter(draftPropTypes);
    setOppsAvailOnly(draftOppsAvailOnly);
    setShowFilter(false);
    openSidebarOnResultsRef.current = true;
  };

  const clearFilter = () => {
    setDraftCity('');
    setDraftBedroom(null);
    setDraftPriceMin('');
    setDraftPriceMax('');
    setDraftFurnish(null);
    setDraftPropTypes([]);
    setDraftOppsAvailOnly(false);
    setSelectedCity('');
    setBedroomFilter(null);
    setPriceFilter({ min: 0, max: 9999 });
    setFurnishFilter(null);
    setPropTypeFilter([]);
    setOppsAvailOnly(false);
    setShowFilter(false);
  };

  const hasActiveFilters =
    selectedCity || bedroomFilter !== null || furnishFilter || propTypeFilter.length > 0 ||
    priceFilter.min > 0 || priceFilter.max < 9999 || oppsAvailOnly;

  const openPanel = (key: Panel) => {
    if (key === 'listings') {
      setActivePanel('listings');
      setPendingPanel(null);
      track('sidebar_open');
      return;
    }
    if (!key) return;
    // Toggle off if clicking the same pending button
    if (pendingPanel === key) {
      setPendingPanel(null);
      return;
    }
    // Enter "waiting for map tap" state
    setActivePanel(null);
    setSelectedCoords(null);
    setPendingPanel(key as FormPanel);
    track(`form_open_${key}`);
  };

  const handleCancel = () => {
    if (activePanel && activePanel !== 'listings') track(`form_abandon_${activePanel}`);
    setActivePanel(null);
    setPendingPanel(null);
    setSelectedCoords(null);
  };

  const handleSuccess = (msg: string) => {
    if (activePanel && activePanel !== 'listings') track(`form_submit_${activePanel}`);
    setShowSuccess(msg);
    setActivePanel(null);
    setSelectedCoords(null);
    setMapRefreshKey(k => k + 1);
    setTimeout(() => setShowSuccess(''), 3500);
  };

  // Map click → always set coords; open pending form or default to pinRent
  const handlePinSelect = useCallback((coords: [number, number]) => {
    setSelectedCoords(coords);
    setPendingPanel(pending => {
      if (pending) {
        setActivePanel(pending);
        return null;
      }
      setActivePanel(prev => {
        if (!prev || prev === 'listings') return 'pinRent';
        return prev;
      });
      return null;
    });
  }, []);

  const handleRentalsUpdate = useCallback((updated: RentPin[], hasMore: boolean) => {
    setRentals(updated);
    setSidebarHasMore(hasMore);
    setSidebarPage(0);
  }, []);

  const handleStatsUpdate = useCallback((count: number, total: number, avg: number) => {
    setStats({ count, total, avg });
  }, []);

  const handleRentalSelect = useCallback((rental: RentPin | null) => {
    setSelectedRental(rental);
    if (rental) {
      setMapSearchCoords([rental.longitude, rental.latitude]);
      if (activePanel !== 'listings') setActivePanel('listings');
    }
  }, [activePanel]);

  const handleDropSeekerPin = useCallback((coords: [number, number], type: 'room' | 'property') => {
    setSelectedCoords(coords);
    setActivePanel(type === 'room' ? 'seekRoom' : 'seekProperty');
  }, []);

  const handleLoadMore = async () => {
    if (sidebarLoading || !sidebarHasMore) return;
    setSidebarLoading(true);
    try {
      const nextPage = sidebarPage + 1;
      const res = await rentalApi.getRentsByCity('Toronto', { skip: nextPage * 50, limit: 50 });
      setRentals(prev => [...prev, ...(res.data.data ?? [])]);
      setSidebarHasMore(res.data.hasMore);
      setSidebarPage(nextPage);
    } catch { /* silently fail */ }
    finally { setSidebarLoading(false); }
  };

  const isFormPanel = activePanel && activePanel !== 'listings';

  return (
    <div className="hp">
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* ── Header ── */}
      <header className="hp-header">
        <div className="hp-logo" aria-label="GTA Rental">
          <span className="hp-logo-mark" aria-hidden="true">GTA</span>
          <span className="hp-logo-text" aria-hidden="true">Rental</span>
        </div>
        <nav className="hp-actions" aria-label="Map tools">
          <button
            className={`header-util-btn${nearMeLoading ? ' loading' : ''}`}
            onClick={handleNearMe}
            disabled={nearMeLoading}
            aria-label={nearMeLoading ? 'Finding your location…' : 'Show rentals near my location'}
          >
            {nearMeLoading
              ? <span className="near-me-spinner" aria-hidden="true" />
              : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2L2 22l10-4 10 4L12 2z" fill="currentColor"/></svg>
            }
            <span className="header-btn-label">Near Me</span>
          </button>
          <button
            className={`header-util-btn${showTransit ? ' on' : ''}`}
            onClick={() => setShowTransit(v => !v)}
            aria-label={showTransit ? 'Hide transit layer' : 'Show transit layer'}
            aria-pressed={showTransit}
          >
            <span aria-hidden="true">🚌</span><span className="header-btn-label"> Transit</span>
          </button>
          <div className="hp-header-sep" aria-hidden="true" />
          <button
            className="how-to-btn"
            onClick={() => { setGuideScreen('guide'); setGuideOpen(true); track('guide_open'); }}
            aria-haspopup="dialog"
          >
            <span className="header-btn-label">How to use?</span>
            <span className="header-btn-short" aria-hidden="true">?</span>
          </button>
          {/* <ThemeToggle /> */}
        </nav>
      </header>

      {/* ── Map area ── */}
      <main id="main-content" className="hp-body" aria-label="GTA Rental Map">
        <div className="hp-map">
          <RentMap
            city={selectedCity}
            onRentalsUpdate={handleRentalsUpdate}
            onRentalSelect={handleRentalSelect}
            selectedRental={selectedRental}
            onPinSelect={handlePinSelect}
            showCreate={!!isFormPanel}
            externalCoordinates={selectedCoords ?? undefined}
            panToCoords={mapSearchCoords ?? undefined}
            priceFilter={priceFilter}
            bedroomFilter={bedroomFilter}
            furnishFilter={furnishFilter}
            propTypeFilter={propTypeFilter}
            oppsAvailOnly={oppsAvailOnly}
            onListingsCountUpdate={setFilteredListingsCount}
            onStatsUpdate={handleStatsUpdate}
            refreshTrigger={mapRefreshKey}
            onDropSeekerPin={handleDropSeekerPin}
            userLocation={nearMeLocation ?? undefined}
            showTransit={showTransit}
            onTransitToggle={() => setShowTransit(v => !v)}
          />
        </div>

        {/* ── Map search overlay ── */}
        <div className="map-search-overlay">
          <div className="map-search-bar" role="search" aria-label="Search neighbourhoods">
            <span className="map-search-icon" aria-hidden="true">⌕</span>
            <AddressAutocomplete
              value={mapSearchValue}
              onChange={setMapSearchValue}
              onPlaceSelect={(addr, coords, city, _postalCode, isSpecificAddress) => {
                setMapSearchValue(addr);
                if (isSpecificAddress) {
                  setMapSearchCoords(coords);
                } else if (city && GTA_CITIES.includes(city)) {
                  setSelectedCity(city);
                  setMapSearchCoords(null);
                } else {
                  setMapSearchCoords(coords);
                  if (city) setSelectedCity(city);
                }
              }}
              placeholder="Search neighbourhood or area…"
              searchTypes={SEARCH_TYPES}
            />
            {mapSearchValue && (
              <button
                className="map-search-clear"
                aria-label="Clear search"
                onClick={() => { setMapSearchValue(''); setMapSearchCoords(null); setSelectedCity(''); }}
              >
                <span aria-hidden="true">✕</span>
              </button>
            )}
          </div>
          <button
            className={`map-filter-btn${hasActiveFilters ? ' active' : ''}`}
            onClick={openFilter}
            aria-haspopup="dialog"
            aria-label={hasActiveFilters ? 'Filters (active)' : 'Filters'}
          >
            <span className="map-filter-icon" aria-hidden="true">⊟</span>
            Filter
            {hasActiveFilters && <span className="map-filter-dot" aria-hidden="true" />}
          </button>
        </div>

        {/* ── Filter panel ── */}
        {showFilter && (
          <div className="map-filter-backdrop" onClick={() => setShowFilter(false)} aria-hidden="true">
            <div
              className="map-filter-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="filter-dialog-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="map-filter-head">
                <span id="filter-dialog-title" className="map-filter-title">Filters</span>
                <button className="map-filter-clear-btn" onClick={clearFilter}>Clear all</button>
                <button className="map-filter-close" onClick={() => setShowFilter(false)} aria-label="Close filters">✕</button>
              </div>

              <div className="map-filter-tabs" role="tablist" aria-label="Filter categories">
                <button
                  role="tab"
                  aria-selected={filterTab === 'flats'}
                  className={`mft${filterTab === 'flats' ? ' on' : ''}`}
                  onClick={() => setFilterTab('flats')}
                >
                  Filter Flats
                </button>
                <button
                  role="tab"
                  aria-selected={filterTab === 'opps'}
                  className={`mft${filterTab === 'opps' ? ' on' : ''}`}
                  onClick={() => setFilterTab('opps')}
                >
                  Opportunities
                </button>
              </div>

              <div className="map-filter-body">
                {/* Area / Neighbourhood — both tabs */}
                <div className="map-filter-section">
                  <label htmlFor="filter-city" className="map-filter-label">AREA / NEIGHBOURHOOD</label>
                  <select
                    id="filter-city"
                    className="map-filter-select"
                    value={draftCity}
                    onChange={e => setDraftCity(e.target.value)}
                  >
                    <option value="">All GTA</option>
                    {GTA_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {filterTab === 'flats' && (
                  <>
                    <div className="map-filter-section">
                      <span id="filter-bedrooms-label" className="map-filter-label">BEDROOMS (BHK)</span>
                      <div className="map-filter-bhk" role="group" aria-labelledby="filter-bedrooms-label">
                        {(['1', '1+', '2', '2+', '3+'] as const).map(b => (
                          <button
                            key={b}
                            type="button"
                            className={`map-bhk-tile${draftBedroom === b ? ' on' : ''}`}
                            aria-pressed={draftBedroom === b}
                            aria-label={`${b} bedroom${b === '1' ? '' : 's'}`}
                            onClick={() => setDraftBedroom(prev => prev === b ? null : b)}
                          >
                            {b}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="map-filter-section">
                      <span className="map-filter-label">RENT RANGE ($/MONTH)</span>
                      <div className="map-filter-range">
                        <label htmlFor="filter-price-min" className="sr-only">Minimum rent</label>
                        <input
                          id="filter-price-min"
                          type="number"
                          className="map-filter-input"
                          placeholder="Min"
                          value={draftPriceMin}
                          onChange={e => setDraftPriceMin(e.target.value)}
                          min="0"
                        />
                        <span className="map-filter-to" aria-hidden="true">to</span>
                        <label htmlFor="filter-price-max" className="sr-only">Maximum rent</label>
                        <input
                          id="filter-price-max"
                          type="number"
                          className="map-filter-input"
                          placeholder="Max"
                          value={draftPriceMax}
                          onChange={e => setDraftPriceMax(e.target.value)}
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="map-filter-section">
                      <span id="filter-furnish-label" className="map-filter-label">FURNISHING</span>
                      <div className="map-filter-pills" role="group" aria-labelledby="filter-furnish-label">
                        {[
                          { val: 'Furnished', label: 'Furnished' },
                          { val: 'Unfurnished', label: 'Unfurnished' },
                        ].map(({ val, label }) => (
                          <button
                            key={val}
                            type="button"
                            className={`map-filter-pill${draftFurnish === val ? ' on' : ''}`}
                            aria-pressed={draftFurnish === val}
                            onClick={() => setDraftFurnish(prev => prev === val ? null : val)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {filterTab === 'opps' && (
                  <>
                    <div className="map-filter-section">
                      <span id="filter-proptype-label" className="map-filter-label">PROPERTY TYPE</span>
                      <div className="map-filter-pills" role="group" aria-labelledby="filter-proptype-label">
                        {PROP_TYPES.map(t => (
                          <button
                            key={t}
                            type="button"
                            className={`map-filter-pill${draftPropTypes.includes(t) ? ' on' : ''}`}
                            aria-pressed={draftPropTypes.includes(t)}
                            onClick={() => setDraftPropTypes(prev => prev.includes(t) ? prev.filter(p => p !== t) : [...prev, t])}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="map-filter-section">
                      <span className="map-filter-label">SHOW ON MAP</span>
                      <div className="map-filter-pills" role="group" aria-label="Map display filter">
                        <button
                          type="button"
                          className={`map-filter-pill${draftOppsAvailOnly ? ' on' : ''}`}
                          aria-pressed={draftOppsAvailOnly}
                          onClick={() => setDraftOppsAvailOnly(v => !v)}
                        >
                          Available rooms &amp; units only
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <button className="map-filter-done" onClick={applyFilter}>Done</button>
            </div>
          </div>
        )}

        {/* ── Pending pin prompt ── */}
        {pendingPanel && (
          <div className="map-pin-prompt" role="status" aria-live="polite">
            <span><span aria-hidden="true">📍</span> Tap anywhere on the map to place your pin</span>
            <button
              className="map-pin-prompt-cancel"
              onClick={() => setPendingPanel(null)}
              aria-label="Cancel pin placement"
            >
              <span aria-hidden="true">✕</span>
            </button>
          </div>
        )}

        {/* ── Bottom status bar ── */}
        <div className="map-bottom-bar" aria-label="Map status">
          <div className="map-bottom-left">
            <span className="map-bottom-crosshair" aria-hidden="true">✕</span>
            <span className="map-bottom-hint" aria-hidden="true">Tap map to pin</span>
            <span className="map-bottom-sep" aria-hidden="true">•</span>
            <button
              type="button"
              className="map-bottom-privacy"
              onClick={() => setPrivacyOpen(true)}
            >
              Privacy Policy
            </button>
            <span className="map-bottom-sep" aria-hidden="true">•</span>
            <button
              type="button"
              className="map-bottom-privacy"
              onClick={() => setTermsOpen(true)}
            >
              Terms of Use
            </button>
          </div>
          <div className="map-bottom-right">
            {stats.count > 0 && (
              <span className="map-bottom-count" aria-live="polite">
                <span className="map-bottom-dot-green" aria-hidden="true" />
                {stats.count.toLocaleString()} rents pinned
                <span className="map-bottom-sep" aria-hidden="true">•</span>
              </span>
            )}
            <button
              className="map-bottom-live"
              aria-pressed={activePanel === 'listings'}
              onClick={() => setActivePanel(activePanel === 'listings' ? null : 'listings')}
            >
              Live stats
            </button>
          </div>
        </div>

        {/* ── Side panel ── */}
        <div className={`hp-panel${activePanel ? ' open' : ''}`}>
          {activePanel === 'listings' && (
            <RentalListingsSidebar
              rentals={rentals}
              selectedRental={selectedRental}
              onSelectRental={handleRentalSelect}
              onViewDetails={setDetailRental}
              totalCount={rentals.length}
              loading={sidebarLoading}
              hasMore={sidebarHasMore}
              onLoadMore={handleLoadMore}
              priceFilter={priceFilter}
              onPriceFilterChange={(min, max) => setPriceFilter({ min, max })}
              bedroomFilter={bedroomFilter}
              onBedroomFilterChange={setBedroomFilter}
              furnishFilter={furnishFilter}
              onFurnishFilterChange={setFurnishFilter}
              propTypeFilter={propTypeFilter}
              onPropTypeFilterChange={setPropTypeFilter}
              onClose={() => setActivePanel(null)}
            />
          )}
          {activePanel === 'pinRent' && (
            <PinRentForm
              externalCoords={selectedCoords ?? undefined}
              onCoordinatesChange={handlePinSelect}
              onSuccess={() => handleSuccess('Rent pinned')}
              onCancel={handleCancel}
              onOpenPrivacy={() => setPrivacyOpen(true)}
              onOpenTerms={() => setTermsOpen(true)}
            />
          )}
          {activePanel === 'postRoom' && (
            <PostRoomForm
              externalCoords={selectedCoords ?? undefined}
              onCoordinatesChange={handlePinSelect}
              onSuccess={() => handleSuccess('Room posted')}
              onCancel={handleCancel}
              onOpenPrivacy={() => setPrivacyOpen(true)}
              onOpenTerms={() => setTermsOpen(true)}
            />
          )}
          {activePanel === 'postProperty' && (
            <PostPropertyForm
              externalCoords={selectedCoords ?? undefined}
              onCoordinatesChange={handlePinSelect}
              onSuccess={() => handleSuccess('Property posted')}
              onCancel={handleCancel}
              onOpenPrivacy={() => setPrivacyOpen(true)}
              onOpenTerms={() => setTermsOpen(true)}
            />
          )}
          {activePanel === 'seekRoom' && (
            <SeekRoomForm
              externalCoords={selectedCoords ?? undefined}
              onCoordinatesChange={handlePinSelect}
              onSuccess={() => handleSuccess('Room search posted')}
              onCancel={handleCancel}
              onOpenPrivacy={() => setPrivacyOpen(true)}
              onOpenTerms={() => setTermsOpen(true)}
            />
          )}
          {activePanel === 'seekProperty' && (
            <SeekPropertyForm
              externalCoords={selectedCoords ?? undefined}
              onCoordinatesChange={handlePinSelect}
              onSuccess={() => handleSuccess('Property search posted')}
              onCancel={handleCancel}
              onOpenPrivacy={() => setPrivacyOpen(true)}
              onOpenTerms={() => setTermsOpen(true)}
            />
          )}
        </div>

        {/* ── No results popup ── */}
        <div role="status" aria-live="polite" aria-atomic="true">
          {showNoResults && (
            <div className="hp-no-results">
              <span aria-hidden="true">📍</span> No properties found in this area
            </div>
          )}
          {(propTypeFilter.length > 0 || oppsAvailOnly) && filteredListingsCount === 0 && (
            <div className="hp-no-results">
              No room/unit is available for the selected filter.
            </div>
          )}
        </div>

        {/* ── Success toast ── */}
        <div role="status" aria-live="polite" aria-atomic="true">
          {showSuccess && (
            <div className="hp-toast"><span aria-hidden="true">✓</span> {showSuccess} successfully!</div>
          )}
        </div>

        {/* ── Right-side action panel ── */}
        <div className={`map-actions-panel${showActionsPanel ? '' : ' collapsed'}`}>
          <button
            className="map-actions-toggle"
            onClick={() => setShowActionsPanel(v => !v)}
            aria-label={showActionsPanel ? 'Hide actions' : 'Show actions'}
            title={showActionsPanel ? 'Hide actions' : 'Show actions'}
          >
            {showActionsPanel ? '›' : '‹'}
          </button>
          {showActionsPanel && (
            <>
              <div className="map-actions-group">
                {FAB_MENU.slice(0, 3).map(item => (
                  <button
                    key={item.key}
                    className={`map-action-btn${activePanel === item.key || pendingPanel === item.key ? ' active' : ''}`}
                    onClick={() => openPanel(item.key)}
                    title={item.title}
                  >
                    <div className="map-action-icon" style={{ background: item.bg }}>
                      <span>{item.icon}</span>
                    </div>
                    <div className="map-action-text">
                      <span className="map-action-label">{item.title}</span>
                      <span className="map-action-sub">{item.sub}</span>
                    </div>
                  </button>
                ))}
              </div>
              <div className="map-action-divider" />
              <div className="map-actions-group">
                {FAB_MENU.slice(3).map(item => (
                  <button
                    key={item.key}
                    className={`map-action-btn${activePanel === item.key || pendingPanel === item.key ? ' active' : ''}`}
                    onClick={() => openPanel(item.key)}
                    title={item.title}
                  >
                    <div className="map-action-icon" style={{ background: item.bg }}>
                      <span>{item.icon}</span>
                    </div>
                    <div className="map-action-text">
                      <span className="map-action-label">{item.title}</span>
                      <span className="map-action-sub">{item.sub}</span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {/* ── Rental detail popup (from sidebar) ── */}
      {detailRental && (
        <PinPopup
          rentals={[detailRental]}
          onClose={() => setDetailRental(null)}
          onDropSeekerPin={handleDropSeekerPin}
        />
      )}

      {/* ── Pin popup opened via delete link ── */}
      {tokenPopupPin && (
        <PinPopup
          rentals={[tokenPopupPin]}
          deleteToken={tokenPopupToken}
          onClose={() => setTokenPopupPin(null)}
          onPinDeleted={() => { setTokenPopupPin(null); setMapRefreshKey(k => k + 1); }}
        />
      )}

      {/* ── Listing/search delete popup opened via delete link ── */}
      {tokenListingEntity && (
        <DeleteListingPopup
          entity={tokenListingEntity}
          onClose={() => setTokenListingEntity(null)}
          onDeleted={() => setTokenListingEntity(null)}
        />
      )}

      {/* ── User guide / onboarding ── */}
      {privacyOpen && (
        <PrivacyPolicyModal onClose={() => setPrivacyOpen(false)} />
      )}

      {termsOpen && (
        <TermsOfUseModal onClose={() => setTermsOpen(false)} />
      )}

      {guideOpen && (
        <UserGuideModal
          initialScreen={guideScreen}
          pinCount={stats.count}
          onClose={() => setGuideOpen(false)}
          onOpenPrivacy={() => setPrivacyOpen(true)}
          onOpenTerms={() => setTermsOpen(true)}
        />
      )}

      {!activePanel && (
        <a
          href="https://www.linkedin.com/in/nishantaggarwal2/"
          target="_blank"
          rel="noopener noreferrer"
          className="built-by-badge"
          aria-label="Built by Nishant — LinkedIn profile"
        >
          Built with ❤️ by <strong>Nishant</strong>
        </a>
      )}
    </div>
  );
};
